import { toRefs } from '@vueuse/shared';
import { useSdk } from '~/sdk';
import type {
  UseReturnsState,
  FetchBulkEligibility,
  FetchEligibility,
  FetchReturnRequests,
  CreateReturnRequest,
} from '~/composables/useReturns/types';

/**
 * @description Composable managing HiWithdrawalManager return/refund requests
 * @returns {@link UseReturns}
 */
export const useReturns = () => {
  const state = useState<UseReturnsState>('useReturns', () => ({
    eligibility: null,
    bulkEligibility: {},
    eligibleProducts: [],
    returnReasons: [],
    requests: [],
    loading: false,
    submitting: false,
    error: null,
  }));

  const { send } = notifications();
  const { $i18n } = useNuxtApp();

  const fetchBulkEligibility: FetchBulkEligibility = async () => {
    state.value.loading = true;

    try {
      const data = await useSdk().commerce.getReturnEligibility({});
      if (data && 'orders' in data && data.orders) {
        const map: Record<number, any> = {};
        for (const row of data.orders) {
          map[row.id_order] = row.availability;
        }
        state.value.bulkEligibility = map;
      }
    } catch (error) {
      useHandleError(error);
    } finally {
      state.value.loading = false;
    }
  };

  const fetchEligibility: FetchEligibility = async (idOrder) => {
    state.value.loading = true;

    try {
      const data = await useSdk().commerce.getReturnEligibility({ id_order: idOrder });
      if (data && 'availability' in data) {
        state.value.eligibility = data.availability ?? null;
        state.value.eligibleProducts = data.products ?? [];
        state.value.returnReasons = data.reasons ?? [];
      }
    } catch (error) {
      useHandleError(error);
    } finally {
      state.value.loading = false;
    }
  };

  const fetchReturnRequests: FetchReturnRequests = async (idOrder) => {
    state.value.loading = true;

    try {
      const data = await useSdk().commerce.getReturnRequests({ id_order: idOrder });
      state.value.requests = data?.requests ?? [];
    } catch (error) {
      useHandleError(error);
    } finally {
      state.value.loading = false;
    }
  };

  const createReturnRequest: CreateReturnRequest = async (params) => {
    state.value.submitting = true;
    state.value.error = null;

    try {
      const data = await useSdk().commerce.createReturnRequest(params);

      if (!data.success) {
        const message = data.message || $i18n.t('Could not submit your return request.');
        state.value.error = message;
        send({ type: 'error', message });
        return false;
      }

      state.value.requests = [
        {
          reference: data.psdata.reference,
          status: data.psdata.status,
          status_label: data.psdata.status_label,
          reason: params.reason,
          reason_label: params.reason,
          message: params.message ?? '',
          order_id: Number(params.id_order),
          date_submitted: data.psdata.date_submitted,
          products: [],
        },
        ...state.value.requests,
      ];

      send({ type: 'success', message: $i18n.t('Return request submitted') });
      return true;
    } catch (error) {
      const message = $i18n.t('Could not submit your return request.') as string;
      state.value.error = message;
      send({ type: 'error', message });
      return false;
    } finally {
      state.value.submitting = false;
    }
  };

  return {
    fetchBulkEligibility,
    fetchEligibility,
    fetchReturnRequests,
    createReturnRequest,
    ...toRefs(state.value),
  };
};
